/**
 * EventsService — Client-side API wrapper for Chargebee Events endpoints.
 *
 * Follows the same URL construction and auth pattern as Api.ts:
 * - Basic Auth with btoa(apiKey)
 * - URL: ${protocol}://${siteName}.${domain}/api/v2/events
 */

import { config } from '@/config';
import type {
  EventApiFilters,
  ListEventsParams,
  ListEventsResponse,
  RetrieveEventResponse,
} from '@/types/events';

function buildEventsBaseUrl(siteName: string): string {
  return `${config.cbProtocol}://${siteName}.${config.cbDomain}/api/v2`;
}

function buildAuthHeaders(apiKey: string): Record<string, string> {
  return {
    Authorization: 'Basic ' + btoa(apiKey),
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
  };
}

/**
 * Maps EventApiFilters to Chargebee query parameter format.
 * e.g. { eventType: { operator: 'in', value: ['a','b'] } }
 *   → { 'event_type[in]': '["a","b"]' }
 */
function buildFilterParams(filters?: EventApiFilters): Record<string, string> {
  if (!filters) return {};
  const params: Record<string, string> = {};

  if (filters.eventType) {
    const { operator, value } = filters.eventType;
    const key = `event_type[${operator}]`;
    params[key] = Array.isArray(value) ? JSON.stringify(value) : value;
  }

  if (filters.source) {
    const { operator, value } = filters.source;
    const key = `source[${operator}]`;
    params[key] = Array.isArray(value) ? JSON.stringify(value) : value;
  }

  if (filters.webhookStatus) {
    const { operator, value } = filters.webhookStatus;
    const key = `webhook_status[${operator}]`;
    params[key] = Array.isArray(value) ? JSON.stringify(value) : value;
  }

  if (filters.occurredAt) {
    const { operator, value } = filters.occurredAt;
    const key = `occurred_at[${operator}]`;
    params[key] = Array.isArray(value) ? JSON.stringify(value) : String(value);
  }

  if (filters.id) {
    const { operator, value } = filters.id;
    const key = `id[${operator}]`;
    params[key] = Array.isArray(value) ? JSON.stringify(value) : value;
  }

  return params;
}

function buildQueryString(params: Record<string, string>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== ''
  );
  if (entries.length === 0) return '';
  return (
    '?' +
    entries
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')
  );
}

/**
 * Fetch a paginated list of events from Chargebee.
 */
export async function listEvents(
  params: ListEventsParams,
  signal?: AbortSignal
): Promise<ListEventsResponse> {
  const baseUrl = buildEventsBaseUrl(params.siteName);
  const queryParams: Record<string, string> = {};

  if (params.limit) {
    queryParams['limit'] = String(params.limit);
  }
  if (params.offset) {
    queryParams['offset'] = params.offset;
  }
  if (params.sortBy) {
    queryParams[`sort_by[${params.sortBy}]`] = 'occurred_at';
  }

  const filterParams = buildFilterParams(params.filters);
  Object.assign(queryParams, filterParams);

  const url = `${baseUrl}/events${buildQueryString(queryParams)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: buildAuthHeaders(params.apiKey),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new EventsApiError(
      `Failed to list events: ${response.status} ${response.statusText}`,
      response.status,
      errorBody
    );
  }

  return response.json();
}

/**
 * Fetch a single event by ID from Chargebee.
 */
export async function retrieveEvent(
  siteName: string,
  apiKey: string,
  eventId: string,
  signal?: AbortSignal
): Promise<RetrieveEventResponse> {
  const baseUrl = buildEventsBaseUrl(siteName);
  const url = `${baseUrl}/events/${encodeURIComponent(eventId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: buildAuthHeaders(apiKey),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new EventsApiError(
      `Failed to retrieve event ${eventId}: ${response.status} ${response.statusText}`,
      response.status,
      errorBody
    );
  }

  return response.json();
}

/**
 * Custom error class for Events API errors with status code.
 */
export class EventsApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: string
  ) {
    super(message);
    this.name = 'EventsApiError';
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }
}
